import React, { FC, useCallback, useMemo, useRef, useState } from 'react';
import { useQuery } from '@apollo/client';
import { addToast } from '~actions';
import BaemingoLogo from '~Assets/Logos/b_logo_white.svg';
import { useStorage } from '~Components/Storage';
import { useTimeManager } from '~Components/TimeManager';
import SelectList from '~ComponentsImproved/SelectList';
import useModal from '~ComponentsImproved/Modal/useModal';
import { useDispatch, useI18n, useLogger, useSelector } from '~Hooks';
import useApi from '~Hooks/useApi';
import useConfirm from '~Hooks/useConfirm';
import { SettingsBlock } from '~Scenes/Settings-v2';
import { Box } from '~Styles';
import { SettingsBlueprints, SettingsBlueprintsVariables } from './__queries__';
import { SETTINGS_BLUEPRINTS_QUERY } from './queries';
import {
  diffBlueprintStorage,
  fromBlueprintStorage,
  isSameBlueprintStorage,
  toBlueprintStorage,
} from './blueprintStorage';
import ApplyBlueprintModal from './ApplyBlueprintModal';
import CreateBlueprint from './CreateBlueprint';
import OperationalAccessButton from './OperationalAccessButton';

/** Blueprints are few per company, fetch them in one go for the picker */
const BLUEPRINT_LIMIT = 10;

/** Code that unlocks the Baemingo wide blueprints and the option to save one */
const OPERATIONAL_ACCESS_CODE = '8888';

interface Props {
  /** Set by the Handy shell; the tablet route renders without it */
  isHandy?: boolean;
}

/**
 * Apply a saved settings blueprint to this device, or save the current settings
 * as a new blueprint for the company.
 */
const BlueprintsSettings: FC<Props> = ({ isHandy = false }) => {
  const { i18n } = useI18n();
  const logger = useLogger('settings-v2-blueprints');
  const dispatch = useDispatch();
  const api = useApi();
  const confirmModal = useConfirm();
  const timeManager = useTimeManager();
  const [storage, commitToStorage, storageLoading] = useStorage();
  const companyId = useSelector((state) => state.company);

  const applyModal = useModal();
  /** Set once the operational access code has been entered; sent with fetches and unlocks Baemingo wide saving */
  const [accessCode, setAccessCode] = useState<string | null>(null);
  /** Name search typed in the picker, sent to the backend */
  const [searchTerm, setSearchTerm] = useState('');
  /** Blueprint picked in the list, awaiting confirmation */
  const [pendingBlueprintId, setPendingBlueprintId] = useState<string | null>(null);

  const blueprintsQuery = useQuery<SettingsBlueprints, SettingsBlueprintsVariables>(
    SETTINGS_BLUEPRINTS_QUERY,
    {
      variables: {
        limit: BLUEPRINT_LIMIT,
        code: accessCode ?? undefined,
        name: searchTerm || undefined,
      },
      fetchPolicy: 'cache-and-network',
    },
  );

  /** Company bound blueprints first, then the Baemingo wide ones */
  const blueprints = useMemo(() => {
    const items = blueprintsQuery.data?.operativeViewer?.settingsBlueprints?.items ?? [];
    return [...items].sort((a, b) => Number(!!b.companyId) - Number(!!a.companyId));
  }, [blueprintsQuery.data]);

  const pendingBlueprint = blueprints.find((blueprint) => blueprint.id === pendingBlueprintId);

  /** Settings that would be overwritten by the pending blueprint */
  const pendingStorage = useMemo(() => fromBlueprintStorage(pendingBlueprint?.storage), [
    pendingBlueprint,
  ]);

  /** This device's settings with device bound keys stripped */
  const currentBlueprintStorage = useMemo(() => toBlueprintStorage(storage), [storage]);

  /** Each blueprint's storage sanitised once per fetch */
  const blueprintStorages = useMemo(
    () => blueprints.map((blueprint) => fromBlueprintStorage(blueprint.storage)),
    [blueprints],
  );

  /** How many settings each blueprint would change on this device, by blueprint id */
  const blueprintDiffCounts = useMemo(() => {
    const counts = new Map<string, number>();
    blueprints.forEach((blueprint, index) => {
      counts.set(blueprint.id, diffBlueprintStorage(storage, blueprintStorages[index]).length);
    });
    return counts;
  }, [blueprints, blueprintStorages, storage]);

  /** A blueprint holding exactly this device's current settings, if any; checked both ways so older blueprints missing newer keys don't match */
  const duplicateBlueprint = useMemo(
    () =>
      blueprints.find((_, index) =>
        isSameBlueprintStorage(currentBlueprintStorage, blueprintStorages[index]),
      ),
    [blueprints, blueprintStorages, currentBlueprintStorage],
  );

  /** Keys whose value will actually change when the pending blueprint is applied, alphabetically for both modals */
  const pendingChanges = useMemo(
    () => diffBlueprintStorage(storage, pendingStorage).sort((a, b) => a.key.localeCompare(b.key)),
    [storage, pendingStorage],
  );

  /** Picker rows rebuilt only when their inputs change, so the memoised SelectList is left alone otherwise */
  const blueprintList = useMemo(
    () =>
      blueprints.map((blueprint) => {
        const diffCount = blueprintDiffCounts.get(blueprint.id) ?? 0;
        return {
          id: blueprint.id,
          name: blueprint.name,
          // Nothing would change, so applying it is pointless
          disabled: diffCount === 0,
          // Baemingo wide blueprints (no company) are told apart by the logo
          icon: blueprint.companyId ? undefined : BaemingoLogo,
          description:
            diffCount === 0
              ? i18n('Settings.Blueprints.MatchesCurrent')
              : i18n('Settings.Blueprints.DiffCount', diffCount),
        };
      }),
    [blueprints, blueprintDiffCounts, i18n],
  );

  const submitAccessCode = useCallback(
    (code: string) => {
      if (code !== OPERATIONAL_ACCESS_CODE) {
        logger.warn('Wrong operational access code entered');
        dispatch(addToast(i18n('Settings.Blueprints.Access.Denied'), true, 'error', true));
        return;
      }

      logger.info('Operational access granted');
      setAccessCode(code);
      dispatch(addToast(i18n('Settings.Blueprints.Access.Granted'), true));
    },
    [dispatch, i18n, logger],
  );

  /** applyModal.toggle changes identity with its state; read it through a ref so the callbacks stay stable */
  const applyModalRef = useRef(applyModal);
  applyModalRef.current = applyModal;

  const selectBlueprint = useCallback(
    (id: string) => {
      logger.info('Selected blueprint', { blueprintId: id });
      setPendingBlueprintId(id);
      applyModalRef.current.toggle();
    },
    [logger],
  );

  const closeApplyModal = useCallback(() => {
    applyModalRef.current.toggle();
    setPendingBlueprintId(null);
  }, []);

  const applyBlueprint = () => {
    if (!pendingBlueprint) return;

    logger.info('Applying blueprint', {
      blueprintId: pendingBlueprint.id,
      blueprintName: pendingBlueprint.name,
      keys: Object.keys(pendingStorage),
      changedKeys: pendingChanges.map((change) => change.key),
    });

    // Server synced time so the record is comparable across devices
    const chosenAt = timeManager.now().toISOString();
    commitToStorage({
      ...pendingStorage,
      chosenBlueprint: { blueprintId: pendingBlueprint.id, chosenAt },
    });
    logger.info('Recorded chosen blueprint', { blueprintId: pendingBlueprint.id, chosenAt });
    dispatch(addToast(i18n('Settings.Blueprints.Applied', pendingBlueprint.name), true));
    applyModal.toggle();
    setPendingBlueprintId(null);
  };

  /** The modal is already closed by CreateBlueprint; the toast is shown once the API call has finished */
  const createBlueprint = async (name: string) => {
    if (!name) return;

    if (!companyId) {
      logger.error('Cannot create blueprint without a company id', { blueprintName: name });
      dispatch(addToast(i18n('Settings.Blueprints.CreateFailed'), true, 'error', true));
      return;
    }

    // With operational access the user picks the scope; blue (right) is customer only, white (left) is all of Baemingo
    let baemingoWide = false;
    if (accessCode) {
      const customerOnly = await confirmModal.confirm(i18n('Settings.Blueprints.Scope.Prompt'), {
        left: i18n('Settings.Blueprints.Scope.Baemingo'),
        right: i18n('Settings.Blueprints.Scope.Customer'),
      });
      baemingoWide = !customerOnly;
    }

    logger.info('Creating blueprint', { blueprintName: name, baemingoWide });

    // A code makes the blueprint Baemingo wide, otherwise it is scoped to the company
    const result = await api.createSettingsBlueprint({
      data:
        baemingoWide && accessCode
          ? { name, storage: currentBlueprintStorage, code: accessCode }
          : { name, storage: currentBlueprintStorage, companyId },
    });

    if (!result.success || !result.data?.createSettingsBlueprint.success) {
      logger.error('Failed to create blueprint', {
        blueprintName: name,
        reason: result.error ?? result.data?.createSettingsBlueprint.message,
      });
      dispatch(addToast(i18n('Settings.Blueprints.CreateFailed'), true, 'error', true));
      return;
    }

    logger.info('Created blueprint', { blueprintName: name });
    dispatch(addToast(i18n('Settings.Blueprints.Created', name), true));
    blueprintsQuery.refetch();
  };

  if (storageLoading) {
    return null;
  }

  return (
    <Box
      padding={isHandy ? 'none' : 'large'}
      marginTop={isHandy ? 'small' : 'none'}
      display="flex"
      flexDirection="column"
      gap={isHandy ? 'small' : 'large'}>
      <SettingsBlock compact={isHandy}>
        <SelectList
          compact={isHandy}
          label={i18n('Settings.Blueprints.Select')}
          keepOrder
          onSearchChange={setSearchTerm}
          isSearchLoading={blueprintsQuery.loading}
          list={blueprintList}
          noneText={i18n('Settings.Blueprints.NoneMatches')}
          // The blueprint matching the current settings reads as selected, which explains why it is disabled
          selectedId={pendingBlueprintId ?? duplicateBlueprint?.id}
          onSelect={selectBlueprint}
        />
      </SettingsBlock>

      <CreateBlueprint
        isHandy={isHandy}
        settingsCount={Object.keys(currentBlueprintStorage).length}
        duplicateName={duplicateBlueprint?.name}
        onCreate={createBlueprint}
      />

      {!accessCode && (
        <Box>
          <OperationalAccessButton isHandy={isHandy} onSubmit={submitAccessCode} />
        </Box>
      )}

      <ApplyBlueprintModal
        isShowing={applyModal.isShowing}
        isHandy={isHandy}
        blueprintName={pendingBlueprint?.name ?? ''}
        changes={pendingChanges}
        onConfirm={applyBlueprint}
        onClose={closeApplyModal}
      />
    </Box>
  );
};

export default BlueprintsSettings;
